import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Modal,
  ActionIcon,
  Card,
  Flex,
  Title,
  TextInput,
  Pagination,
  Table,
  SegmentedControl,
  Text,
  LoadingOverlay,
} from "@mantine/core";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import { IconExclamationMark } from "@tabler/icons-react";
import { useSelector } from "react-redux";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

export default function BulkCommunicationMesssages({
  id,
  refresh,
  variety,
  info,
}) {
  const [searchValue, setSearchValue] = useState("");
  const [statusValue, setStatusValue] = useState("entire");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });

  const usersLocked = info.enqueued ? true : false;
  const showFilters = !usersLocked;

  async function fetchUsers(pagination, { signal }) {
    if (!id) {
      throw new Error("No ID provided");
    }

    const req = {
      page_size: pagination.pageSize,
      page: pagination.pageIndex === -1 ? 0 : pagination.pageIndex,
      search_value: searchValue,
      status: statusValue,
      bulk_communicator_id: id,
    };
    const url = usersLocked
      ? `/bulk-communicators/${id}/retrieve-users/`
      : `/bulk-communicators/${id}/retrieve-messages/`;

    const { data } = await axios.post(url, req, { signal });
    return data.response[0];
  }

  const dataQuery = useQuery({
    queryKey: [
      "bulkCommunicationMessages",
      id,
      pagination,
      searchValue,
      statusValue,
      refresh,
    ],
    queryFn: ({ signal }) => fetchUsers(pagination, { signal }),
    placeholderData: keepPreviousData, // don't have 0 rows flash while changing pages/loading next page
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 0, // Always consider data stale to ensure fresh requests
    gcTime: 0, // Don't cache data to ensure fresh requests
  });

  // Query is working - data will be available when request completes

  const columns = [
    {
      header: "Name",
      // accessorKey: "recipient_name",
      id: "name",
      enableSorting: false,
      cell: (info) => {
        const { original } = info.row;
        const hasErrorData =
          original.error_data && JSON.stringify(original.error_data) !== "{}";
        if (hasErrorData) {
          return (
            <Flex gap="xs" align="center">
              <ErrorListModal items={[{ ...original.error_data }]} />
              <span>{original.name}</span>
            </Flex>
          );
        } else {
          return original.name;
        }
      },
    },
    { header: "Email", accessorKey: "email", enableSorting: false },
    { header: "Phone", accessorKey: "phone", enableSorting: false },
    // { header: "Date", accessorKey: "updated_at", enableSorting: false },
    {
      header: "Status",
      accessorKey: "message_status",
      enableSorting: false,
    },
  ]
    .filter((f) =>
      statusValue !== "entire" ? !["Status"].includes(f.header) : true
    )
    .filter((f) =>
      variety === 2
        ? !["Email"].includes(f.header)
        : !["Phone"].includes(f.header)
    );

  const table = useReactTable({
    data: dataQuery.data?.data || [],
    columns,
    manualPagination: true,
    enableMultiSort: false,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      pagination,
      globalFilter: searchValue,
    },
    rowCount: dataQuery.data?.total || 0,
    onPaginationChange: setPagination,
    initialState: {
      pagination: {
        pageSize: 20,
      },
    },
    manualFiltering: true,
  });

  const isLoading = dataQuery.isLoading || dataQuery.isFetching;

  const statusOptions = [
    { label: "All", value: "entire" },
    { label: "Waiting", value: "waiting" },
    { label: "Queued", value: "queued" },
    { label: "Sent", value: "sent" },
    { label: "Delivered", value: "delivered" },
    { label: "Opened", value: "opened" },
    { label: "Bounced", value: "bounced" },
    { label: "Errored", value: "errored" },
  ].filter((f) =>
    variety === 2
      ? ["entire", "waiting", "sent", "delivered"].includes(f.value)
      : true
  );

  // Reset pagination when search or status changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [searchValue, statusValue]);

  // // Handle pagination changes
  // useEffect(() => {
  //   const currentPageIndex = table.getState().pagination.pageIndex;
  //   if (currentPageIndex !== pageIndex) {
  //     setPageIndex(currentPageIndex);
  //   }
  // }, [table.getState().pagination.pageIndex]);

  // function onSearchSubmit() {
  //   setPageIndex(0);
  // }

  return (
    <div>
      <Flex gap="xs" align="center" mb="sm">
        <Title order={5}>Users</Title>
      </Flex>
      <Text fw={600} size="xs" mb="lg">
        {info.enqueued
          ? "*You cannot add users once enqueued. Remove it from the queue to add users."
          : "Once enqueued, users will show up here."}
      </Text>
      <Flex style={{ width: "100%" }} gap="xs" align="center">
        <TextInput
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder="Search..."
          style={{
            flexGrow: 1,
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              onSearchSubmit();
            }
          }}
        />
      </Flex>
      {showFilters && (
        <SegmentedControl
          value={statusValue}
          data={statusOptions.filter((f) =>
            f.variety !== 2 ? !["queued"].includes(f.value) : true
          )}
          size="xs"
          onChange={(e) => setStatusValue(e)}
          mt="sm"
        />
      )}
      <Card mt="sm" style={{ position: "relative" }}>
        <LoadingOverlay visible={isLoading} overlayProps={{ radius: "sm" }} />
        <Table.ScrollContainer minWidth={1000}>
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                {table.getFlatHeaders().map((header) => (
                  <Table.Th key={header.id}>
                    {header.column.columnDef.header}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {!isLoading && table.getRowModel().rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={table.getFlatHeaders().length}>
                    No users yet
                  </Table.Td>
                </Table.Tr>
              )}
              {table.getRowModel().rows.map((row) => {
                return (
                  <Table.Tr key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <Table.Td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </Table.Td>
                      );
                    })}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
      <Pagination
        value={pagination.pageIndex + 1}
        onChange={(e) => setPagination({ ...pagination, pageIndex: e - 1 })}
        total={dataQuery.data?.page_count || 1}
        mt="lg"
      />
    </div>
  );
}

const ErrorListModal = ({ items = [] }) => {
  const [isOpen, setOpen] = useState(false);
  const managerInfo = useSelector((state) => state.manager);

  function onClose() {
    setOpen(false);
  }

  return (
    <>
      <ActionIcon
        onClick={() => setOpen(true)}
        size="xs"
        radius="xl"
        color="red"
      >
        <IconExclamationMark />
      </ActionIcon>
      <Modal opened={isOpen} onClose={onClose} size="lg">
        {items.map((item, i) => (
          <div key={i}>
            <Table striped>
              <Table.Tbody>
                {Object.keys(item)
                  .filter((f) =>
                    managerInfo ? ["Description", "message"].includes(f) : true
                  )
                  .map((m, ii) => (
                    <Table.Tr key={ii}>
                      <Table.Td>{m}</Table.Td>
                      <Table.Td>{JSON.stringify(item[m])}</Table.Td>
                    </Table.Tr>
                  ))}
              </Table.Tbody>
            </Table>
          </div>
        ))}
      </Modal>
    </>
  );
};
